<app-catalog-page-card title="BOND DOCUMENTATION">
  <app-catalog-table-toolbar
    datatableId="kt_bond_documentation_datatable"
    searchPlaceholder="Search bond documentation records..."
    addButtonLabel="Add Bond Documentation"
    addModalId="create_bond_documentation_modal"
    refreshAriaLabel="Refresh bond documentation"
    [disableRefresh]="isLoading()"
    (refresh)="loadRows()"
  ></app-catalog-table-toolbar>

  <app-catalog-crud-modal
    modalId="create_bond_documentation_modal"
    title="Create Bond Documentation"
    submitLabel="Create Bond Documentation"
    [submitting]="isCreating()"
    [formError]="createFormError()"
    (submit)="onCreate()"
    (cancel)="resetCreateForm()"
  >
    <div modal-body>
      <form class="grid gap-4" autocomplete="off" [formGroup]="createForm">
        <div class="grid gap-1.5">
          <label class="kt-form-label" for="create-bond-documentation-name">
            Name <span class="catalog-required-marker" aria-hidden="true">*</span>
          </label>
          <input
            id="create-bond-documentation-name"
            type="text"
            class="kt-input"
            maxlength="30"
            formControlName="name"
            [attr.aria-invalid]="shouldShowCreateFieldError('name')"
          />

          @if (shouldShowCreateRequiredError('name')) {
            <div class="catalog-field-error">Name is required.</div>
          }

          @if (shouldShowCreateMaxLengthError('name')) {
            <div class="catalog-field-error">Name cannot exceed 30 characters.</div>
          }
        </div>

        <div class="grid gap-1.5">
          <label class="kt-form-label" for="create-bond-documentation-description">Description</label>
          <input
            id="create-bond-documentation-description"
            type="text"
            class="kt-input"
            maxlength="50"
            formControlName="description"
            [attr.aria-invalid]="shouldShowCreateFieldError('description')"
          />

          @if (shouldShowCreateMaxLengthError('description')) {
            <div class="catalog-field-error">Description cannot exceed 50 characters.</div>
          }
        </div>
      </form>
    </div>
  </app-catalog-crud-modal>

  <button
    type="button"
    class="hidden"
    aria-hidden="true"
    tabindex="-1"
    data-kt-modal-toggle="#edit_bond_documentation_modal"
  ></button>

  <app-catalog-crud-modal
    modalId="edit_bond_documentation_modal"
    title="Edit Bond Documentation"
    submitLabel="Save Changes"
    [submitting]="isUpdating()"
    [formError]="editFormError()"
    (submit)="onUpdate()"
    (cancel)="resetEditForm()"
  >
    <div modal-body>
      <form class="grid gap-4" autocomplete="off" [formGroup]="editForm">
        <div class="grid gap-1.5">
          <label class="kt-form-label" for="edit-bond-documentation-name">
            Name <span class="catalog-required-marker" aria-hidden="true">*</span>
          </label>
          <input
            id="edit-bond-documentation-name"
            type="text"
            class="kt-input"
            maxlength="30"
            formControlName="name"
            [attr.aria-invalid]="shouldShowEditFieldError('name')"
          />

          @if (shouldShowEditRequiredError('name')) {
            <div class="catalog-field-error">Name is required.</div>
          }

          @if (shouldShowEditMaxLengthError('name')) {
            <div class="catalog-field-error">Name cannot exceed 30 characters.</div>
          }
        </div>

        <div class="grid gap-1.5">
          <label class="kt-form-label" for="edit-bond-documentation-description">Description</label>
          <input
            id="edit-bond-documentation-description"
            type="text"
            class="kt-input"
            maxlength="50"
            formControlName="description"
            [attr.aria-invalid]="shouldShowEditFieldError('description')"
          />

          @if (shouldShowEditMaxLengthError('description')) {
            <div class="catalog-field-error">Description cannot exceed 50 characters.</div>
          }
        </div>
      </form>
    </div>
  </app-catalog-crud-modal>

  <button
    type="button"
    class="hidden"
    aria-hidden="true"
    tabindex="-1"
    data-kt-modal-toggle="#delete_bond_documentation_confirm_modal"
  ></button>

  <app-catalog-delete-confirm-modal
    modalId="delete_bond_documentation_confirm_modal"
    title="Remove Bond Documentation"
    message="Are you sure you want to remove this bond documentation?"
    confirmLabel="Remove Bond Documentation"
    [confirming]="deletingId() !== null"
    (confirm)="confirmDelete()"
    (cancel)="cancelDelete()"
  ></app-catalog-delete-confirm-modal>

  @if (hasAlert()) {
    <div
      class="kt-alert kt-alert-light"
      [class.kt-alert-success]="alertVariant() === 'success'"
      [class.kt-alert-destructive]="alertVariant() === 'destructive'"
      role="alert"
    >
      <div class="kt-alert-icon">
        <i class="ki-filled ki-information" aria-hidden="true"></i>
      </div>

      <div class="grow">
        <div class="kt-alert-title"><strong>{{ alertTitle() }}</strong></div>

        @if (alertMessage()) {
          <div class="kt-alert-text">{{ alertMessage() }}</div>
        }
      </div>

      <div class="kt-alert-toolbar">
        <div class="kt-alert-actions">
          <button
            type="button"
            class="kt-alert-close"
            aria-label="Dismiss alert"
            (click)="dismissAlert()"
          >
            <i class="ki-filled ki-cross" aria-hidden="true"></i>
          </button>
        </div>
      </div>
    </div>
  }

  @if (isLoading()) {
    <div class="catalog-loading" role="status" aria-live="polite" aria-label="Loading bond documentation">
      <span class="catalog-loading-spinner" aria-hidden="true"></span>
      <span class="catalog-loading-text">Loading...</span>
    </div>
  }

  @if (!isLoading() && errorMessage()) {
    <div class="kt-alert kt-alert-destructive flex items-center justify-between gap-3">
      <span>{{ errorMessage() }}</span>
      <button type="button" class="kt-btn kt-btn-sm kt-btn-light" (click)="loadRows()">Retry</button>
    </div>
  }

  @if (!isLoading() && !errorMessage()) {
    <div
      id="kt_bond_documentation_datatable"
      class="kt-card-table"
      data-kt-datatable="true"
      data-kt-datatable-page-size="20"
      data-kt-datatable-state-save="true"
      data-kt-datatable-state-namespace="bond-documentation-datatable"
      (click)="onTableActionClick($event)"
    >
      <div class="kt-table-wrapper kt-scrollable">
        <table class="kt-table" data-kt-datatable-table="true">
          <thead>
            <tr>
              <th scope="col" data-kt-datatable-column="name">
                <span class="kt-table-col">
                  <span class="kt-table-col-label">Name</span>
                  <span class="kt-table-col-sort"></span>
                </span>
              </th>
              <th scope="col" data-kt-datatable-column="description">
                <span class="kt-table-col">
                  <span class="kt-table-col-label">Description</span>
                  <span class="kt-table-col-sort"></span>
                </span>
              </th>
              <th
                scope="col"
                data-kt-datatable-column="actions"
                data-kt-datatable-column-sort="false"
                class="text-end"
              >
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            @if (rows().length === 0) {
              <tr>
                <td colspan="3" class="text-center text-secondary-foreground py-6">
                  No bond documentation found.
                </td>
              </tr>
            } @else {
              @for (item of rows(); track item.id) {
                <tr>
                  <td>{{ item.name }}</td>
                  <td>{{ item.description || '-' }}</td>
                  <td class="text-end">
                    <span class="inline-flex gap-2.5">
                      <button
                        type="button"
                        class="kt-btn kt-btn-sm kt-btn-icon kt-btn-outline"
                        aria-label="Edit bond documentation"
                        data-action="edit-bond-documentation"
                        [attr.data-bond-documentation-id]="item.id"
                        [disabled]="deletingId() !== null || isUpdating()"
                      >
                        <i class="ki-filled ki-pencil" aria-hidden="true"></i>
                      </button>
                      <button
                        type="button"
                        class="kt-btn kt-btn-sm kt-btn-icon kt-btn-outline"
                        aria-label="Remove bond documentation"
                        data-action="delete-bond-documentation"
                        [attr.data-bond-documentation-id]="item.id"
                        [disabled]="deletingId() !== null"
                      >
                        <i class="ki-filled ki-trash" aria-hidden="true"></i>
                      </button>
                    </span>
                  </td>
                </tr>
              }
            }
          </tbody>
        </table>
      </div>

      <div class="flex items-center justify-between gap-4 flex-wrap px-5 py-4">
        <div data-kt-datatable-info="true" class="kt-datatable-info text-sm text-secondary-foreground"></div>

        <div class="kt-datatable-length flex items-center gap-3">
          <select data-kt-datatable-size="true" class="kt-select kt-select-sm w-20"></select>
          <div data-kt-datatable-pagination="true" class="kt-datatable-pagination flex items-center gap-2"></div>
        </div>
      </div>
    </div>
  }
</app-catalog-page-card>
